import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { pointsAPI } from '@/api/points';
import { 
  getAuthToken, 
  getPortalType, 
  getUserData, 
  isAuthenticated, 
  getActivePortal 
} from '@/utils/auth';
import { getAnonymousUserPreferences, getAnonymousUserType } from '@/utils/anonymousAuth';
import { getGuestStats, updateGuestStats, addGuestXP, addGuestGems } from '@/utils/guestStorage';

const PointsContext = createContext(null);

export const usePoints = () => {
  const context = useContext(PointsContext);
  if (!context) {
    throw new Error('usePoints must be used within a PointsProvider');
  }
  return context;
};

export const PointsProvider = ({ children, initialData = null }) => {
  const [points, setPoints] = useState(initialData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const location = useLocation();

  // Initial hydration if data is provided later (e.g. from a root bootstrap)
  useEffect(() => {
    if (initialData && !points) {
      setPoints(initialData);
    }
  }, [initialData, points]);
  const hasCheckedDailyLoginRef = useRef(false); // Track if we've checked today

  /**
   * Fetch current student's points from backend API
   * For guests, return points from localStorage
   * Only fetches for students - returns null for other user types
   */
  const refreshPoints = useCallback(async () => {
    const portal = getActivePortal();
    const isAuthed = isAuthenticated(portal);
    const isActuallyGuest = localStorage.getItem('is_guest_session') === 'true';
    const isGuest = !isAuthed && (isActuallyGuest || !!getAnonymousUserPreferences());
    
    if (isGuest) {
      // Load guest stats from localStorage
      const guestStats = getGuestStats();
      setPoints({
        total_xp: guestStats.total_xp || 0,
        gems: guestStats.gems || 0,
        hearts: guestStats.hearts || 5,
        current_streak: guestStats.current_streak || 0,
        is_guest: true
      });
      return guestStats;
    }

    if (!isAuthed) {
      // Not authenticated and not guest - clear points
      setPoints(null);
      return null;
    }

    // Only fetch points for students
    const userData = getUserData(portal);
    if (!userData || userData.user_type !== 'student') {
      // Not a student - clear points and don't make API call
      setPoints(null);
      return null;
    }

    // Only fetch if portal is student
    if (portal !== 'student') {
      setPoints(null);
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('[PointsContext] Fetching points from API...');
      const data = await pointsAPI.getPoints();
      console.log('[PointsContext] Points fetched:', data);
      setPoints(data);
      // Notify other tabs about the update
      localStorage.setItem('points_data_updated', JSON.stringify(data));
      return data;
    } catch (err) {
      console.error('[PointsContext] Error fetching points:', err);
      setError(err.message || 'Failed to fetch points');
      // Don't clear points on error - keep existing data
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Award points for an activity (and refresh points)
   * For guests, add to localStorage
   * @param {Object} data - { amount_xp, amount_gems, reason, related_object_type, related_object_id }
   * @returns {Promise<Object>} Award result
   */
  const awardPoints = useCallback(async (data, updatedPoints = null) => {
    const portal = getPortalType(window.location.pathname || '');
    const token = getAuthToken(portal);
    const isGuest = !token && !!getAnonymousUserPreferences();

    if (isGuest) {
      // For guests, update localStorage
      let updatedStats = {};
      if (data.amount_xp) {
        updatedStats = addGuestXP(data.amount_xp);
      }
      if (data.amount_gems) {
        updatedStats = addGuestGems(data.amount_gems);
      }
      // Trigger refresh to update UI
      await refreshPoints();
      return updatedStats;
    }

    if (updatedPoints) {
      console.log('[PointsContext] Updating points from provided data (skipping fetch)');
      setPoints(updatedPoints);
      localStorage.setItem('points_data_updated', JSON.stringify(updatedPoints));
      return { success: true };
    }

    try {
      const result = await pointsAPI.awardPoints(data);
      // Refresh points after awarding
      await refreshPoints();
      return result;
    } catch (err) {
      console.error('Error awarding points:', err);
      throw err;
    }
  }, [refreshPoints]);

  /**
   * Manually update points data (e.g. from an API response that included student_points)
   */
  const updatePoints = useCallback((data) => {
    if (data) {
      console.log('[PointsContext] Manually updating points data');
      setPoints(data);
      localStorage.setItem('points_data_updated', JSON.stringify(data));
    }
  }, []);

  /**
   * Claim daily login bonus
   */
  const claimDailyLogin = useCallback(async () => {
    const isActuallyGuest = localStorage.getItem('is_guest_session') === 'true';
    if (isActuallyGuest) return { claimed: false };

    try {
      const result = await pointsAPI.claimDailyLogin();
      // Refresh points after claiming
      await refreshPoints();
      return result;
    } catch (err) {
      // Silently handle "already claimed" errors
      if (err.message?.includes('already claimed') || err.message?.includes('400')) {
        console.log('[PointsContext] Daily login already claimed today');
        return { claimed: false };
      }
      console.error('Error claiming daily login:', err);
      throw err;
    }
  }, [refreshPoints]);

  /**
   * Check and claim daily login bonus automatically (only for students)
   */
  const checkAndClaimDailyLogin = useCallback(async () => {
    const portal = getActivePortal();
    const token = getAuthToken(portal);
    const isActuallyGuest = localStorage.getItem('is_guest_session') === 'true';
    
    if (!token || isActuallyGuest) {
      return;
    }

    // Only claim for students
    const userData = getUserData(portal);
    if (!userData || userData.user_type !== 'student') {
      return;
    }

    // Check if we've already checked today (prevent multiple calls)
    const today = new Date().toDateString();
    const lastCheckKey = `daily_login_check_${portal}_${today}`;
    if (hasCheckedDailyLoginRef.current || localStorage.getItem(lastCheckKey)) {
      return;
    }

    try {
      // Mark as checked (even if it fails, don't retry today)
      hasCheckedDailyLoginRef.current = true;
      localStorage.setItem(lastCheckKey, 'true');

      // Claim daily login bonus
      const result = await claimDailyLogin();
      
      if (result?.claimed) {
        console.log('[PointsContext] Daily login bonus claimed:', result.xp_awarded, 'XP');
      }
    } catch (err) {
      // Silently fail - don't show error to user
      console.log('[PointsContext] Could not claim daily login bonus:', err.message);
    }
  }, [claimDailyLogin]);

  /**
   * Buy heart refill with gems
   */
  const buyHeartRefill = useCallback(async () => {
    const isActuallyGuest = localStorage.getItem('is_guest_session') === 'true';
    if (isActuallyGuest) {
      // Guest heart refill (local)
      const stats = getGuestStats();
      if (stats.gems >= 50) {
        const updated = updateGuestStats({ 
          gems: stats.gems - 50,
          hearts: 5
        });
        await refreshPoints();
        return { success: true, ...updated };
      }
      throw new Error('Not enough gems');
    }

    try {
      const result = await pointsAPI.buyHeartRefill();
      // Refresh points after purchase
      await refreshPoints();
      return result;
    } catch (err) {
      console.error('Error buying heart refill:', err);
      throw err;
    }
  }, [refreshPoints]);

  // Listen for login success event to reset/refresh points data (e.g., when logging in as a NEW user after a GUEST session)
  useEffect(() => {
    const handleLogin = (e) => {
      console.log('[PointsContext] User logged in, refreshing points for portal:', e.detail?.portal);
      if (e.detail?.portal === 'student') {
        refreshPoints().catch(() => {});
      } else {
        setPoints(null);
      }
    };
    
    window.addEventListener('login_success', handleLogin);
    return () => window.removeEventListener('login_success', handleLogin);
  }, [refreshPoints]);

  // Fetch points on mount
  useEffect(() => {
    // Optimization: Skip fetching if we already have initialPoints from Bootstrap
    if (initialData) {
      console.log('[PointsContext] Skipping initial fetch (hydrated from Bootstrap)');
      return;
    }

    const portal = getPortalType(window.location.pathname || '');
    const token = getAuthToken(portal);
    const isGuest = !token && !!getAnonymousUserPreferences();
    const userData = getUserData(portal);
    
    // Only fetch points if user is a student or guest
    if (portal !== 'student') {
      setPoints(null);
      return;
    }

    if (isGuest) {
      // Load guest stats from localStorage
      const guestStats = getGuestStats();
      setPoints({
        total_xp: guestStats.total_xp || 0,
        gems: guestStats.gems || 0,
        hearts: guestStats.hearts || 5,
        current_streak: guestStats.current_streak || 0,
        is_guest: true
      });
      return;
    }

    if (!userData || userData.user_type !== 'student') {
      setPoints(null);
      return;
    }

    const fetchAndClaim = async () => {
      const data = await refreshPoints();
      
      // After points are loaded, check and claim daily login bonus
      if (data) {
        setTimeout(() => {
          checkAndClaimDailyLogin();
        }, 500);
      }
    };

    fetchAndClaim();
  }, [refreshPoints, checkAndClaimDailyLogin, initialData]);

  // Sync points across tabs via localStorage change events
  useEffect(() => {
    const handleStorageChange = (e) => {
      // Listen for points updates from other tabs
      if (e.key === 'points_data_updated') {
        try {
          const data = JSON.parse(e.newValue);
          if (data) {
            console.log('[PointsContext] Syncing points from another tab');
            setPoints(data);
          }
        } catch (err) {
          console.error('[PointsContext] Sync error:', err);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Reset daily login check on new day
  useEffect(() => {
    const today = new Date().toDateString();
    const portal = getPortalType(window.location.pathname || '');
    const lastCheckKey = `daily_login_check_${portal}_${today}`;
    
    // Check if we have a check from a different day
    // Clean up old keys (keys with different dates)
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(`daily_login_check_${portal}_`) && key !== lastCheckKey) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    // If we don't have today's check, reset the ref
    if (!localStorage.getItem(lastCheckKey)) {
      hasCheckedDailyLoginRef.current = false;
    }
  }, [location.pathname]);

  const value = {
    points,
    loading,
    error,
    refreshPoints,
    updatePoints,
    awardPoints,
    claimDailyLogin,
    buyHeartRefill,
  };

  return (
    <PointsContext.Provider value={value}>
      {children}
    </PointsContext.Provider>
  );
};
